import React from 'react';
import { Calendar, CheckCircle2, Clock, Hourglass } from 'lucide-react';
import { CommencementTrancheMaster } from '../types/masterData';

interface TrancheTimelineProps {
  activeTrancheFilter: string;
  onSelectTranche: (tranche: string) => void;
  tranches?: CommencementTrancheMaster[];
}

export default function TrancheTimeline({
  activeTrancheFilter,
  onSelectTranche,
  tranches: propTranches
}: TrancheTimelineProps) {
  const defaultTranches = [
    {
      id: '2025-11-13',
      name: 'Tranche 1',
      date: '13 Nov 2025',
      status: 'In Force',
      headline: 'Statutory Foundation & DPB',
      description: 'Establishment of Data Protection Board of India, inquiry powers, definitions, digital office framework.',
      colorClass: 'emerald',
      activeBorder: 'border-emerald-500 ring-2 ring-emerald-500/20 bg-emerald-500/10 dark:bg-emerald-500/15',
      badgeBg: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
    },
    {
      id: '2026-11-13',
      name: 'Tranche 2',
      date: '13 Nov 2026',
      status: 'Consent Managers',
      headline: 'Consent Architecture & Interoperability',
      description: 'Registration of Consent Managers under s.6(9), interoperability standards, technical onboarding.',
      colorClass: 'sky',
      activeBorder: 'border-sky-500 ring-2 ring-sky-500/20 bg-sky-500/10 dark:bg-sky-500/15',
      badgeBg: 'bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30'
    },
    {
      id: '2027-05-13',
      name: 'Tranche 3',
      date: '13 May 2027',
      status: 'Core Enterprise Duties',
      headline: 'Enterprise Compliance & Penalties Live',
      description: 'All substantive duties: Notice & Consent, Principal Rights, Retention/Erasure, Breach Notification, SDF Obligations & Penalties up to ₹250 Cr.',
      colorClass: 'amber',
      activeBorder: 'border-amber-500 ring-2 ring-amber-500/20 bg-amber-500/10 dark:bg-amber-500/15',
      badgeBg: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30'
    }
  ];

  const tranches = (propTranches && propTranches.length > 0)
    ? propTranches.map((t, idx) => {
        const colors = ['emerald', 'sky', 'amber', 'indigo', 'purple', 'rose'];
        const color = t.colorClass || colors[idx % colors.length];
        return {
          ...t,
          colorClass: color,
          activeBorder: `border-${color}-500 ring-2 ring-${color}-500/20 bg-${color}-500/10 dark:bg-${color}-500/15`,
          badgeBg: `bg-${color}-500/15 text-${color}-700 dark:text-${color}-400 border-${color}-500/30`
        };
      })
    : defaultTranches;

  return (
    <div className="p-4 sm:p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-accent" />
          <h3 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100">
            Statutory Commencement Tranches & Effective Dates
          </h3>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onSelectTranche('all')}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
              activeTrancheFilter === 'all'
                ? 'bg-accent text-white shadow-xs'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            Show All Tranches
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
        {tranches.map((t) => {
          const isSelected = activeTrancheFilter === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onSelectTranche(isSelected ? 'all' : t.id)}
              className={`p-3.5 rounded-xl border text-left transition-all ${
                isSelected
                  ? t.activeBorder
                  : 'bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700/80 hover:border-slate-400 dark:hover:border-slate-600'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-extrabold text-xs text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${
                    t.colorClass === 'emerald' ? 'bg-emerald-500' :
                    t.colorClass === 'sky' ? 'bg-sky-500' : 'bg-amber-500'
                  }`} />
                  {t.name}
                </span>
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-mono font-bold border ${t.badgeBg}`}>
                  {t.date}
                </span>
              </div>

              <div className="mt-2 font-semibold text-xs text-slate-800 dark:text-slate-200">
                {t.headline}
              </div>
              <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed line-clamp-2">
                {t.description}
              </p>

              <div className="mt-2.5 flex items-center justify-between text-[10px] text-slate-400 dark:text-slate-500 font-mono">
                <span>{t.status}</span>
                <span className="text-accent font-semibold">{isSelected ? '✓ Filtering View' : 'Click to filter'}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
